import React from 'react';
import { ImpactLogo, OptioLogoFull, SlideWrapper, GlassCard, SectionLabel } from './SharedComponents';

// ============ SLIDE 1: TITLE ============
export const TitleSlide = ({ slide }) => (
  <SlideWrapper slide={slide} background={
    <div className="fixed inset-0 z-0">
      <div className="absolute inset-0 bg-[#030308]" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[1600px] h-[1000px] bg-[#0068ff] opacity-[0.1] blur-[200px] rounded-full" />
      <div className="absolute top-0 right-0 w-[700px] h-[700px] bg-[#0052cc] opacity-[0.06] blur-[150px] rounded-full" />
      <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-[#003d99] opacity-[0.05] blur-[120px] rounded-full" />
      <div className="absolute inset-0 opacity-[0.03]" style={{
        backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(0,104,255,0.3) 1px, transparent 0)',
        backgroundSize: '60px 60px'
      }} />
      <svg className="absolute inset-0 w-full h-full opacity-[0.04]" viewBox="0 0 1920 1080">
        <line x1="960" y1="0" x2="960" y2="1080" stroke="#0068ff" strokeWidth="0.5" />
        <line x1="0" y1="540" x2="1920" y2="540" stroke="#0068ff" strokeWidth="0.5" />
        <circle cx="960" cy="540" r="300" fill="none" stroke="#0068ff" strokeWidth="0.5" strokeDasharray="8 8" />
        <circle cx="960" cy="540" r="500" fill="none" stroke="#0068ff" strokeWidth="0.3" strokeDasharray="4 12" />
      </svg>
      <div className="absolute top-16 left-16 w-24 h-24">
        <div className="absolute top-0 left-0 w-full h-[1px] bg-gradient-to-r from-[#0068ff]/40 to-transparent" />
        <div className="absolute top-0 left-0 h-full w-[1px] bg-gradient-to-b from-[#0068ff]/40 to-transparent" />
      </div>
      <div className="absolute top-16 right-16 w-24 h-24">
        <div className="absolute top-0 right-0 w-full h-[1px] bg-gradient-to-l from-[#0068ff]/40 to-transparent" />
        <div className="absolute top-0 right-0 h-full w-[1px] bg-gradient-to-b from-[#0068ff]/40 to-transparent" />
      </div>
      <div className="absolute bottom-16 left-16 w-24 h-24">
        <div className="absolute bottom-0 left-0 w-full h-[1px] bg-gradient-to-r from-[#0068ff]/40 to-transparent" />
        <div className="absolute bottom-0 left-0 h-full w-[1px] bg-gradient-to-t from-[#0068ff]/40 to-transparent" />
      </div>
      <div className="absolute bottom-16 right-16 w-24 h-24">
        <div className="absolute bottom-0 right-0 w-full h-[1px] bg-gradient-to-l from-[#0068ff]/40 to-transparent" />
        <div className="absolute bottom-0 right-0 h-full w-[1px] bg-gradient-to-t from-[#0068ff]/40 to-transparent" />
      </div>
    </div>
  }>
    <div className="relative z-10 flex flex-col items-center justify-center h-full">
      <div className="animate-scale-in mb-8">
        <ImpactLogo />
      </div>
      <div className="flex items-center gap-4 mb-6 animate-fade-in" style={{ animationDelay: '0.3s' }}>
        <div className="w-24 h-[1px] bg-gradient-to-r from-transparent to-[#0068ff]/60" />
        <div className="w-2 h-2 bg-[#0068ff] rotate-45" />
        <div className="w-24 h-[1px] bg-gradient-to-l from-transparent to-[#0068ff]/60" />
      </div>
      <p className="text-white/80 text-xl md:text-2xl lg:text-3xl font-light tracking-wide animate-slide-up" style={{ animationDelay: '0.2s' }}>
        {slide.subtitle}
      </p>
      <div className="absolute bottom-12 animate-fade-in" style={{ animationDelay: '0.5s' }}>
        <OptioLogoFull />
      </div>
    </div>
  </SlideWrapper>
);

// ============ SLIDE 2: BIG IDEA ============
const CardIcon = ({ type }) => {
  const icons = {
    verify: <svg viewBox="0 0 24 24" className="w-7 h-7" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" strokeLinecap="round" strokeLinejoin="round" /></svg>,
    reward: <svg viewBox="0 0 24 24" className="w-7 h-7" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" strokeLinecap="round" strokeLinejoin="round" /></svg>,
    network: <svg viewBox="0 0 24 24" className="w-7 h-7" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" strokeLinecap="round" strokeLinejoin="round" /></svg>,
    measure: <svg viewBox="0 0 24 24" className="w-7 h-7" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" strokeLinecap="round" strokeLinejoin="round" /></svg>
  };
  return icons[type] || null;
};

export const BigIdeaSlide = ({ slide }) => {
  const cardColors = ['#0068ff', '#8b5cf6', '#06b6d4', '#22c55e'];
  return (
    <SlideWrapper slide={slide} background={
      <div className="fixed inset-0 z-0">
        <div className="absolute inset-0 bg-gradient-to-b from-[#030308] via-[#0a0a18] to-[#030308]" />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1200px] h-[600px] bg-[#0068ff] opacity-[0.07] blur-[180px] rounded-full" />
        <div className="absolute bottom-0 right-0 w-[600px] h-[600px] bg-[#8b5cf6] opacity-[0.04] blur-[150px] rounded-full" />
        <svg className="absolute inset-0 w-full h-full opacity-[0.02]">
          <defs><pattern id="dotGrid2" width="40" height="40" patternUnits="userSpaceOnUse"><circle cx="20" cy="20" r="1" fill="#0068ff" /></pattern></defs>
          <rect width="100%" height="100%" fill="url(#dotGrid2)" />
        </svg>
        <div className="absolute top-1/2 right-0 -translate-y-1/2 w-[400px] h-[400px] rounded-full border border-[#0068ff]/[0.06]" />
        <div className="absolute top-1/2 right-[-100px] -translate-y-1/2 w-[600px] h-[600px] rounded-full border border-[#0068ff]/[0.03]" />
      </div>
    }>
      <div className="relative z-10 flex flex-col items-center justify-center h-full px-8">
        <div className="mb-8 animate-fade-in"><OptioLogoFull /></div>
        <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-center max-w-5xl leading-tight mb-14 animate-slide-up">
          {slide.headline}
        </h2>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 w-full max-w-6xl">
          {slide.cards.map((card, idx) => (
            <GlassCard key={idx} delay={0.2 + idx * 0.1} className="p-6 group hover:border-white/[0.15] transition-all duration-300">
              <div className="absolute top-0 left-0 right-0 h-[2px] rounded-t-2xl" style={{ background: `linear-gradient(90deg, ${cardColors[idx]}80, transparent)` }} />
              <div className="relative mb-4" style={{ color: cardColors[idx] }}><CardIcon type={card.icon} /></div>
              <h3 className="relative text-lg font-semibold text-white mb-2">{card.title}</h3>
              <p className="relative text-sm text-white/50 leading-relaxed">{card.description}</p>
            </GlassCard>
          ))}
        </div>
      </div>
    </SlideWrapper>
  );
};

// ============ SLIDE 3: EVOLUTION PYRAMID ============
export const EvolutionPyramidSlide = ({ slide }) => {
  const levels = [
    { label: 'Impact', era: 'Tomorrow', color: '#0068ff', highlight: true, desc: 'Verified contribution' },
    { label: 'Attention', era: '2010s', color: '#8b5cf6', highlight: false, desc: 'Engagement metrics' },
    { label: 'Knowledge', era: '1990s', color: '#06b6d4', highlight: false, desc: 'Information economy' },
    { label: 'Labor', era: '1900s', color: '#f59e0b', highlight: false, desc: 'Industrial output' },
    { label: 'Extraction', era: 'Ancient', color: '#6b7280', highlight: false, desc: 'Raw resources' },
  ];
  return (
    <SlideWrapper slide={slide} background={
      <div className="fixed inset-0 z-0">
        <div className="absolute inset-0 bg-gradient-to-b from-[#030308] via-[#080812] to-[#030308]" />
        <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[700px] h-[700px] bg-[#0068ff] opacity-[0.08] blur-[200px] rounded-full" />
        <div className="absolute bottom-0 left-1/4 w-[400px] h-[400px] bg-[#f59e0b] opacity-[0.03] blur-[120px] rounded-full" />
        <div className="absolute bottom-0 right-1/4 w-[400px] h-[400px] bg-[#8b5cf6] opacity-[0.03] blur-[120px] rounded-full" />
        <svg className="absolute inset-0 w-full h-full opacity-[0.03]" viewBox="0 0 1920 1080">
          <polygon points="960,100 1600,900 320,900" fill="none" stroke="#0068ff" strokeWidth="1" strokeDasharray="6 6" />
        </svg>
      </div>
    }>
      <div className="relative z-10 flex flex-col items-center justify-center h-full px-8">
        <SectionLabel delay={0}>Economic Evolution</SectionLabel>
        <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-center mt-5 mb-3 animate-slide-up">{slide.title}</h2>
        <p className="text-white/40 text-lg mb-10 animate-fade-in" style={{ animationDelay: '0.1s' }}>{slide.subtitle}</p>
        <div className="relative flex flex-col items-center gap-3">
          {levels.map((level, idx) => {
            const w = 220 + idx * 110;
            return (
              <div key={idx} className="animate-slide-up" style={{ animationDelay: `${0.15 + idx * 0.08}s` }}>
                <div className="relative rounded-xl overflow-hidden transition-all duration-300" style={{
                  width: `${w}px`,
                  background: `${level.color}${level.highlight ? '20' : '0a'}`,
                  border: `1px solid ${level.color}${level.highlight ? '60' : '30'}`,
                  boxShadow: level.highlight ? `0 0 40px ${level.color}30, inset 0 0 30px ${level.color}10` : 'none'
                }}>
                  {level.highlight && <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full animate-[shimmer_3s_infinite]" />}
                  <div className="relative py-3.5 px-5 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className="text-xl font-bold" style={{ color: level.highlight ? '#fff' : level.color }}>{level.label}</span>
                      {level.highlight && (
                        <span className="relative flex h-2.5 w-2.5">
                          <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75" style={{ background: level.color }} />
                          <span className="relative inline-flex rounded-full h-2.5 w-2.5" style={{ background: level.color }} />
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-[10px] text-white/40">{level.desc}</span>
                      <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded" style={{ color: level.color, background: `${level.color}20` }}>{level.era}</span>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
          <div className="absolute -left-20 top-1/2 -translate-y-1/2">
            <div className="flex flex-col items-center gap-1 text-[#0068ff]/60">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" /></svg>
              <span className="text-[9px] font-bold tracking-[0.2em] uppercase" style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}>Value</span>
            </div>
          </div>
        </div>
      </div>
    </SlideWrapper>
  );
};

// ============ SLIDE 4: ATTENTION ECONOMY ============
const AttentionIcon = ({ type }) => {
  const icons = {
    heart: <svg viewBox="0 0 24 24" className="w-8 h-8" fill="currentColor"><path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/></svg>,
    share: <svg viewBox="0 0 24 24" className="w-8 h-8" fill="currentColor"><path d="M18 16.08c-.76 0-1.44.3-1.96.77L8.91 12.7c.05-.23.09-.46.09-.7s-.04-.47-.09-.7l7.05-4.11c.54.5 1.25.81 2.04.81 1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3c0 .24.04.47.09.7L8.04 9.81C7.5 9.31 6.79 9 6 9c-1.66 0-3 1.34-3 3s1.34 3 3 3c.79 0 1.5-.31 2.04-.81l7.12 4.16c-.05.21-.08.43-.08.65 0 1.61 1.31 2.92 2.92 2.92s2.92-1.31 2.92-2.92-1.31-2.92-2.92-2.92z"/></svg>,
    cursor: <svg viewBox="0 0 24 24" className="w-8 h-8" fill="currentColor"><path d="M4 4l16 6.5-6.5 2-2 6.5L4 4z"/></svg>,
    clock: <svg viewBox="0 0 24 24" className="w-8 h-8" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>
  };
  return icons[type] || null;
};

export const AttentionEconomySlide = ({ slide }) => {
  const colors = ['#ec4899', '#8b5cf6', '#f43f5e', '#0068ff'];
  return (
    <SlideWrapper slide={slide} background={
      <div className="fixed inset-0 z-0">
        <div className="absolute inset-0 bg-gradient-to-br from-[#030308] via-[#1a0a1a] to-[#030308]" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[1000px] h-[800px] bg-[#ec4899] opacity-[0.06] blur-[200px] rounded-full" />
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-[#8b5cf6] opacity-[0.05] blur-[150px] rounded-full" />
        <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-[#f43f5e] opacity-[0.04] blur-[120px] rounded-full" />
        {[...Array(12)].map((_, i) => (
          <div key={i} className="absolute w-1.5 h-1.5 rounded-full animate-float" style={{
            left: `${10 + (i * 7)}%`, top: `${15 + (i % 4) * 22}%`,
            background: colors[i % 4], opacity: 0.15,
            animationDelay: `${i * 0.4}s`, animationDuration: `${3 + (i % 3)}s`
          }} />
        ))}
      </div>
    }>
      <div className="relative z-10 flex flex-col items-center justify-center h-full px-8">
        <SectionLabel color="#ec4899" delay={0}>Where we are now</SectionLabel>
        <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-center mt-5 mb-3 animate-slide-up">{slide.title}</h2>
        <p className="text-white/40 text-lg mb-12 animate-fade-in" style={{ animationDelay: '0.1s' }}>{slide.subtitle}</p>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 w-full max-w-5xl">
          {slide.metrics.map((metric, idx) => (
            <GlassCard key={idx} delay={0.2 + idx * 0.1} className="p-7 text-center group hover:scale-[1.03] transition-transform duration-300" style={{
              background: `linear-gradient(135deg, ${colors[idx]}10, ${colors[idx]}05)`,
              borderColor: `${colors[idx]}25`
            }}>
              <div className="w-14 h-14 rounded-xl mx-auto mb-4 flex items-center justify-center" style={{ background: `${colors[idx]}15`, color: colors[idx] }}>
                <AttentionIcon type={metric.icon} />
              </div>
              <div className="text-4xl font-bold mb-1" style={{ color: colors[idx] }}>{metric.value}</div>
              <div className="text-white/30 text-xs mb-2 uppercase tracking-wider">{metric.subtext}</div>
              <div className="text-white text-lg font-semibold">{metric.label}</div>
            </GlassCard>
          ))}
        </div>
        <div className="mt-10 flex items-center gap-4 animate-fade-in" style={{ animationDelay: '0.6s' }}>
          <div className="h-[1px] w-16 bg-gradient-to-r from-transparent to-[#ec4899]/40" />
          <span className="text-white/30 text-sm tracking-wide">The currency of the digital age</span>
          <div className="h-[1px] w-16 bg-gradient-to-l from-transparent to-[#ec4899]/40" />
        </div>
      </div>
    </SlideWrapper>
  );
};

// ============ SLIDE 5: HIDDEN INCENTIVE ============
const LoopIcon = ({ type }) => {
  const icons = {
    content: <svg viewBox="0 0 24 24" className="w-7 h-7" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18M9 21V9"/></svg>,
    engagement: <svg viewBox="0 0 24 24" className="w-7 h-7" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M14 9V5a3 3 0 00-6 0v4M5 9h14l1 12H4L5 9z"/></svg>,
    algorithm: <svg viewBox="0 0 24 24" className="w-7 h-7" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="4" y="4" width="6" height="6" rx="1"/><rect x="14" y="4" width="6" height="6" rx="1"/><rect x="4" y="14" width="6" height="6" rx="1"/><rect x="14" y="14" width="6" height="6" rx="1"/></svg>,
    amplify: <svg viewBox="0 0 24 24" className="w-7 h-7" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M11 5L6 9H2v6h4l5 4V5zM19.07 4.93a10 10 0 010 14.14M15.54 8.46a5 5 0 010 7.07"/></svg>
  };
  return icons[type] || null;
};

export const HiddenIncentiveSlide = ({ slide }) => {
  const nodeColors = ['#8b5cf6', '#ec4899', '#f43f5e', '#f97316'];
  const nodeLabels = ['Content', 'Engagement', 'Algorithm', 'Amplify'];
  const nodeIcons = ['content', 'engagement', 'algorithm', 'amplify'];
  const positions = [
    { top: '10px', left: '50%', ml: '-52px' },
    { top: '50%', right: '10px', mt: '-52px' },
    { bottom: '10px', left: '50%', ml: '-52px' },
    { top: '50%', left: '10px', mt: '-52px' },
  ];
  return (
    <SlideWrapper slide={slide} background={
      <div className="fixed inset-0 z-0">
        <div className="absolute inset-0 bg-gradient-to-br from-[#030308] via-[#0a0a12] to-[#030308]" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[900px] h-[900px] bg-[#f43f5e] opacity-[0.05] blur-[200px] rounded-full" />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[400px] bg-[#8b5cf6] opacity-[0.03] blur-[150px] rounded-full" />
        <svg className="absolute inset-0 w-full h-full opacity-[0.015]">
          <defs><pattern id="circuit5" width="80" height="80" patternUnits="userSpaceOnUse"><path d="M0 40h30m20 0h30M40 0v30m0 20v30" stroke="#fff" strokeWidth="0.3" fill="none"/><circle cx="40" cy="40" r="2" fill="#f43f5e" opacity="0.5"/></pattern></defs>
          <rect width="100%" height="100%" fill="url(#circuit5)" />
        </svg>
      </div>
    }>
      <div className="relative z-10 flex flex-col items-center justify-center h-full px-8">
        <SectionLabel color="#f43f5e" delay={0}>The Problem</SectionLabel>
        <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-center max-w-4xl mt-5 mb-10 animate-slide-up">{slide.headline}</h2>
        <div className="relative w-[460px] h-[460px] animate-scale-in" style={{ animationDelay: '0.2s' }}>
          <svg className="absolute inset-0 w-full h-full animate-[spin_25s_linear_infinite]" viewBox="0 0 460 460">
            <circle cx="230" cy="230" r="175" fill="none" stroke="url(#rGrad5)" strokeWidth="1.5" strokeDasharray="15 8" />
            <defs><linearGradient id="rGrad5" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" stopColor="#8b5cf6" stopOpacity="0.5"/><stop offset="50%" stopColor="#f43f5e" stopOpacity="0.5"/><stop offset="100%" stopColor="#f97316" stopOpacity="0.5"/></linearGradient></defs>
          </svg>
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-24 h-24 rounded-full flex items-center justify-center z-10" style={{ background: 'linear-gradient(135deg, #f43f5e15, #8b5cf615)', border: '1px solid #f43f5e30' }}>
            <div className="text-center">
              <svg viewBox="0 0 24 24" className="w-7 h-7 mx-auto text-[#f43f5e]" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="12" cy="12" r="3"/><path d="M12 2v4M12 18v4M2 12h4M18 12h4"/></svg>
              <span className="text-[9px] text-white/50 font-bold mt-1 block">ENGINE</span>
            </div>
          </div>
          {nodeLabels.map((label, idx) => (
            <div key={idx} className="absolute animate-slide-up" style={{
              ...positions[idx], marginLeft: positions[idx].ml, marginTop: positions[idx].mt,
              animationDelay: `${0.3 + idx * 0.1}s`
            }}>
              <div className="w-[104px] h-[104px] rounded-2xl p-3 flex flex-col items-center justify-center transition-all duration-300 hover:scale-110"
                style={{ background: `linear-gradient(135deg, ${nodeColors[idx]}18, ${nodeColors[idx]}08)`, border: `1px solid ${nodeColors[idx]}35`, boxShadow: `0 0 25px ${nodeColors[idx]}15` }}>
                <div style={{ color: nodeColors[idx] }}><LoopIcon type={nodeIcons[idx]} /></div>
                <span className="text-white text-xs font-semibold mt-1.5">{label}</span>
              </div>
            </div>
          ))}
          <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 460 460">
            <defs>{nodeColors.map((c, i) => <marker key={i} id={`arr${i}`} markerWidth="7" markerHeight="5" refX="6" refY="2.5" orient="auto"><polygon points="0 0,7 2.5,0 5" fill={c} opacity="0.7"/></marker>)}</defs>
            <path d="M 275 62 Q 370 90 390 175" fill="none" stroke="#8b5cf6" strokeWidth="1.5" strokeOpacity="0.5" markerEnd="url(#arr0)"/>
            <path d="M 390 285 Q 370 370 275 398" fill="none" stroke="#ec4899" strokeWidth="1.5" strokeOpacity="0.5" markerEnd="url(#arr1)"/>
            <path d="M 185 398 Q 90 370 70 285" fill="none" stroke="#f43f5e" strokeWidth="1.5" strokeOpacity="0.5" markerEnd="url(#arr2)"/>
            <path d="M 70 175 Q 90 90 185 62" fill="none" stroke="#f97316" strokeWidth="1.5" strokeOpacity="0.5" markerEnd="url(#arr3)"/>
          </svg>
        </div>
        <GlassCard delay={0.7} className="mt-6 px-8 py-3" style={{ background: '#f43f5e10', borderColor: '#f43f5e25' }}>
          <span className="text-[#f43f5e] text-base font-medium">{slide.insight}</span>
        </GlassCard>
      </div>
    </SlideWrapper>
  );
};
